import { useState, useCallback, useRef, useEffect } from 'react';
import { VideoElement, PlayerAction } from '../../types/player';
import { attemptPlay } from '../../utils/media-utils';

export const usePlaybackControl = (
  videoElement: VideoElement,
  isInitialized: boolean,
  autoPlay: boolean,
  autoPlayAttemptedRef: React.MutableRefObject<boolean>,
  setIsPlaying: (playing: boolean) => void,
  hidePreview: () => void,
) => {
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentAction, setCurrentAction] = useState<PlayerAction>(null);
  const [showToggleIcon, setShowToggleIcon] = useState(false);

  // Control action icon display timeout
  const actionIconTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const toggleIconTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Ensure video element has proper event listeners
  useEffect(() => {
    if (!videoElement) return;

    // Add play/pause event listeners to update state
    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    videoElement.addEventListener('play', handlePlay);
    videoElement.addEventListener('pause', handlePause);

    // Apply current playback speed
    videoElement.playbackRate = playbackSpeed;

    return () => {
      videoElement.removeEventListener('play', handlePlay);
      videoElement.removeEventListener('pause', handlePause);
    };
  }, [videoElement, setIsPlaying, playbackSpeed]);

  // Toggle play/pause
  const togglePlayPause = useCallback(() => {
    if (!videoElement) {
      return;
    }

    if (videoElement.paused) {
      attemptPlay(videoElement)
        .then((success) => {
          if (success) {
            setIsPlaying(true);
          } else {
          }
        })
        .catch((err) => {});
    } else {
      videoElement.pause();
      setIsPlaying(false);
    }
  }, [videoElement, setIsPlaying]);

  // Handle seeking forward
  const handleForward = useCallback(() => {
    if (!videoElement) {
      return;
    }

    const newTime = Math.min(videoElement.currentTime + 10, videoElement.duration);
    videoElement.currentTime = newTime;

    // Show action icon
    setCurrentAction('forward');
    if (actionIconTimeoutRef.current) {
      clearTimeout(actionIconTimeoutRef.current);
    }
    actionIconTimeoutRef.current = setTimeout(() => {
      setCurrentAction(null);
    }, 1000);
  }, [videoElement]);

  // Handle seeking backward
  const handleRewind = useCallback(() => {
    if (!videoElement) {
      return;
    }

    const newTime = Math.max(videoElement.currentTime - 10, 0);
    videoElement.currentTime = newTime;

    // Show action icon
    setCurrentAction('rewind');
    if (actionIconTimeoutRef.current) {
      clearTimeout(actionIconTimeoutRef.current);
    }
    actionIconTimeoutRef.current = setTimeout(() => {
      setCurrentAction(null);
    }, 1000);
  }, [videoElement]);

  // Change playback speed
  const handlePlaybackSpeedChange = useCallback(() => {
    if (!videoElement) {
      return;
    }

    const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % speeds.length;
    const newSpeed = speeds[nextIndex];

    setPlaybackSpeed(newSpeed);
    videoElement.playbackRate = newSpeed;
  }, [videoElement, playbackSpeed]);

  // Handle container click for play/pause
  const handleContainerClick = useCallback(() => {
    togglePlayPause();

    // Hide storyboard preview when clicking on video
    hidePreview();

    // Show play/pause icon
    setShowToggleIcon(true);
    if (toggleIconTimeoutRef.current) {
      clearTimeout(toggleIconTimeoutRef.current);
    }
    toggleIconTimeoutRef.current = setTimeout(() => {
      setShowToggleIcon(false);
    }, 1000);
  }, [togglePlayPause, hidePreview]);
  // Attempt autoplay when player is ready
  const attemptAutoplay = useCallback(() => {
    if (!videoElement || !autoPlay || autoPlayAttemptedRef.current || !isInitialized) {
      if (!videoElement) {
      }
      if (!autoPlay) {
      }
      if (autoPlayAttemptedRef.current) {
      }
      if (!isInitialized) {
      }
      return;
    }

    autoPlayAttemptedRef.current = true;

    attemptPlay(videoElement)
      .then((success) => {
        if (success) {
          setIsPlaying(true);
        } else {
        }
      })
      .catch((err) => {});
  }, [videoElement, autoPlay, isInitialized, autoPlayAttemptedRef, setIsPlaying]);

  // Clean up timeouts
  useEffect(() => {
    return () => {
      if (actionIconTimeoutRef.current) {
        clearTimeout(actionIconTimeoutRef.current);
      }
      if (toggleIconTimeoutRef.current) {
        clearTimeout(toggleIconTimeoutRef.current);
      }
    };
  }, []);

  return {
    playbackSpeed,
    currentAction,
    showToggleIcon,
    togglePlayPause,
    handleForward,
    handleRewind,
    handlePlaybackSpeedChange,
    handleContainerClick,
    attemptAutoplay,
  };
};
